/**
 * Wayfinding for an IMPORTED floor plan.
 *
 * The sample estate can build a corridor graph because its corridors are
 * declared geometry. A drawing has no such declaration — so the walkable model
 * comes from the other half of what segmentation found: the neutral, uncoloured
 * regions. Those are the malls, promenades and atria. Rasterised to a coarse
 * grid, they ARE the navigable space.
 *
 * Two things make the resulting routes read like a real wayfinder rather than
 * a maze solver:
 *
 *   CENTRE PULL. A raw grid Dijkstra hugs walls, because scraping the corner is
 *   the shortest path. A distance transform gives every cell its clearance from
 *   the nearest obstruction, and cells near a wall are surcharged — so the path
 *   runs down the middle of the mall the way a person walks it.
 *
 *   STRAIGHTENING. A grid path is a staircase of one-cell steps. The shared
 *   router simplifies each leg before narrating it, so a straight walk reads as
 *   "head east for 90 m" instead of forty alternating turns.
 */

import { makeGraph, createRouter } from '../data/router';
import { unpackBits } from './regionKinds';
import { VERTICAL_TYPES } from './amenities';
import { pointInPolygon } from './geometry';

/** Cells nearer than this many cells to an obstruction are surcharged. */
const CLEARANCE_TARGET = 3;
/** How much a wall-hugging cell costs over an open one, at most. */
const CENTRE_PULL = 1.4;
/** How far from a unit we will look for walkable space, in cells. */
const MAX_DOOR_SEARCH = 14;
/** Two vertical markers on different floors link if within this many metres. */
const CORE_LINK_METRES = 12;

/** Normalised plan coordinates -> mall-space metres, centred on the plan. */
const toWorld = (nx, ny, widthM, heightM) => ({
    x: (nx - 0.5) * widthM,
    z: (ny - 0.5) * heightM,
});

/**
 * Chamfer 3-4 distance transform: for each walkable cell, its distance in
 * cell-units to the nearest non-walkable cell.
 */
function clearanceMap(cells, w, h) {
    const INF = 1e9;
    const d = new Float32Array(w * h);
    for (let i = 0; i < cells.length; i++) d[i] = cells[i] ? INF : 0;

    const at = (x, y) => ((x < 0 || y < 0 || x >= w || y >= h) ? 0 : d[(y * w) + x]);

    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = (y * w) + x;
            if (!d[i]) continue;
            d[i] = Math.min(
                d[i],
                at(x - 1, y) + 3, at(x, y - 1) + 3,
                at(x - 1, y - 1) + 4, at(x + 1, y - 1) + 4,
            );
        }
    }
    for (let y = h - 1; y >= 0; y--) {
        for (let x = w - 1; x >= 0; x--) {
            const i = (y * w) + x;
            if (!d[i]) continue;
            d[i] = Math.min(
                d[i],
                at(x + 1, y) + 3, at(x, y + 1) + 3,
                at(x + 1, y + 1) + 4, at(x - 1, y + 1) + 4,
            );
        }
    }
    for (let i = 0; i < d.length; i++) d[i] /= 3;   // back into cell units
    return d;
}

/** Spiral outward from a cell until a walkable one is found. */
function nearestWalkable(cells, w, h, gx, gy, clearance) {
    if (gx >= 0 && gy >= 0 && gx < w && gy < h && cells[(gy * w) + gx]) return { gx, gy };
    let best = null;
    let bestScore = Infinity;
    for (let r = 1; r <= MAX_DOOR_SEARCH; r++) {
        for (let dy = -r; dy <= r; dy++) {
            for (let dx = -r; dx <= r; dx++) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
                const x = gx + dx;
                const y = gy + dy;
                if (x < 0 || y < 0 || x >= w || y >= h) continue;
                if (!cells[(y * w) + x]) continue;
                // Among equidistant candidates prefer the one with more room,
                // so a door lands in the mall rather than in a doorway pinch.
                const score = Math.hypot(dx, dy) - (clearance[(y * w) + x] * 0.25);
                if (score < bestScore) { bestScore = score; best = { gx: x, gy: y }; }
            }
        }
        if (best) return best;
    }
    return null;
}

/**
 * Where a unit's shopfront meets the corridor.
 *
 * Anchoring the endpoint at the centroid would draw the first leg of every
 * route straight out through the middle of the shop. Walking the ray from the
 * centroid toward the corridor and stopping at the outline puts the pin on the
 * door instead.
 */
function frontageToward(polygon, from, target) {
    let lastInside = from;
    const steps = 48;
    for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const p = [from[0] + ((target[0] - from[0]) * t), from[1] + ((target[1] - from[1]) * t)];
        if (pointInPolygon(p, polygon)) lastInside = p;
        else return p;
    }
    return lastInside;
}

/**
 * Build a router for a published plan.
 *
 * @param {object} plan  as persisted by importedPlan.savePlan
 * @param {Array}  units the commercial-enriched units (same ids as plan floors)
 */
export function buildPlanRouter(plan, units) {
    if (!plan || !Array.isArray(plan.floors)) return null;

    const graph = makeGraph();
    const endpoints = [];
    const floors = plan.floors.map((f) => ({ id: f.id, name: f.name, short: f.short, level: f.level, y: f.y }));
    const unitsByFloor = new Map();
    units.forEach((u) => {
        if (!unitsByFloor.has(u.floor)) unitsByFloor.set(u.floor, []);
        unitsByFloor.get(u.floor).push(u);
    });

    // Rank floors within THIS plan rather than by their absolute level. An
    // import is often two sheets — say Ground and First with no mezzanine — and
    // keying the link off absolute levels leaves them two apart, so the lift
    // never connects and cross-floor routing silently returns nothing.
    const rankOf = new Map(
        plan.floors
            .slice()
            .sort((a, b) => (a.level || 0) - (b.level || 0))
            .map((f, i) => [f.id, i]),
    );

    const verticalMarkers = [];   // { floor, rank, node, type }
    const landmarks = [];         // { floor, x, z, label }
    let walkableFloors = 0;

    plan.floors.forEach((floor) => {
        const widthM = floor.widthM || 100;
        const heightM = floor.heightM || 100;
        const grid = floor.walkable;
        const onFloor = unitsByFloor.get(floor.id) || [];

        if (!grid || !grid.bits) {
            // No circulation was traced for this level. Its units still exist and
            // still show on the map; they simply have nothing to route along, and
            // the panel says so rather than silently returning no route.
            return;
        }

        const { w, h } = grid;
        const cells = unpackBits(grid.bits, w * h);
        const clearance = clearanceMap(cells, w, h);
        const cellW = widthM / w;
        const cellH = heightM / h;
        walkableFloors += 1;

        const cellNode = (gx, gy) => {
            const p = toWorld((gx + 0.5) / w, (gy + 0.5) / h, widthM, heightM);
            return graph.addNode(floor.id, p.x, p.z, 'corridor');
        };

        // Surcharge for hugging a wall: 0 in open space, CENTRE_PULL at the edge.
        const penalty = (gx, gy) => {
            const c = clearance[(gy * w) + gx];
            return CENTRE_PULL * Math.max(0, 1 - (c / CLEARANCE_TARGET));
        };

        // Walkable cells, 8-connected.
        for (let gy = 0; gy < h; gy++) {
            for (let gx = 0; gx < w; gx++) {
                if (!cells[(gy * w) + gx]) continue;
                const a = cellNode(gx, gy);
                const pa = penalty(gx, gy);
                const NEIGHBOURS = [[1, 0], [0, 1], [1, 1], [1, -1]];   // half the ring; edges are undirected
                NEIGHBOURS.forEach(([dx, dy]) => {
                    const nx = gx + dx;
                    const ny = gy + dy;
                    if (nx < 0 || ny < 0 || nx >= w || ny >= h) return;
                    if (!cells[(ny * w) + nx]) return;
                    const b = cellNode(nx, ny);
                    const metres = Math.hypot(dx * cellW, dy * cellH);
                    const factor = 1 + ((pa + penalty(nx, ny)) / 2);
                    graph.addEdge(a, b, 'walk', metres * factor);
                });
            }
        }

        // Doors.
        onFloor.forEach((u) => {
            const nx = (u.center.x / widthM) + 0.5;
            const ny = (u.center.z / heightM) + 0.5;
            const gx = Math.max(0, Math.min(w - 1, Math.floor(nx * w)));
            const gy = Math.max(0, Math.min(h - 1, Math.floor(ny * h)));
            const cell = nearestWalkable(cells, w, h, gx, gy, clearance);
            if (!cell) return;

            const target = toWorld((cell.gx + 0.5) / w, (cell.gy + 0.5) / h, widthM, heightM);
            const front = u.polygon && u.polygon.length >= 3
                ? frontageToward(u.polygon, [u.center.x, u.center.z], [target.x, target.z])
                : [u.center.x, u.center.z];

            const door = graph.addNode(floor.id, front[0], front[1], 'door', u.name);
            door.unitId = u.id;
            const spur = graph.addNode(floor.id, target.x, target.z, 'corridor');
            graph.addEdge(door, spur, 'walk');

            // Keep the map in step with where the route actually enters.
            u.frontage = { x: door.x, z: door.z };

            endpoints.push({
                id: `unit:${u.id}`,
                label: u.name,
                sub: `${u.code} · ${u.floorName}`,
                floor: floor.id,
                nodeKey: door.key,
                kind: 'unit',
            });
            if (u.isAnchor) landmarks.push({ floor: floor.id, x: u.center.x, z: u.center.z, label: u.name });
        });

        // Amenities.
        (floor.amenities || []).forEach((a, i) => {
            const gx = Math.max(0, Math.min(w - 1, Math.floor(a.x * w)));
            const gy = Math.max(0, Math.min(h - 1, Math.floor(a.y * h)));
            const cell = nearestWalkable(cells, w, h, gx, gy, clearance);
            if (!cell) return;

            const p = toWorld(a.x, a.y, widthM, heightM);
            const node = graph.addNode(floor.id, p.x, p.z, 'amenity', a.name || `${a.type} ${i + 1}`);
            node.amenityType = a.type;
            node.amenityId = a.id;
            const target = toWorld((cell.gx + 0.5) / w, (cell.gy + 0.5) / h, widthM, heightM);
            graph.addEdge(node, graph.addNode(floor.id, target.x, target.z, 'corridor'), 'walk');

            endpoints.push({
                id: `amenity:${floor.id}:${a.id}`,
                label: node.label,
                sub: `${a.type} · ${floor.name}`,
                floor: floor.id,
                nodeKey: node.key,
                kind: 'amenity',
            });
            landmarks.push({ floor: floor.id, x: p.x, z: p.z, label: node.label });

            if (VERTICAL_TYPES.has(a.type)) {
                verticalMarkers.push({ floor: floor.id, rank: rankOf.get(floor.id), node, type: a.type });
            }
        });
    });

    if (!walkableFloors) return null;

    // Stitch floors: a lift or escalator drawn at the same place on two levels
    // is the same core. Nothing else connects levels, which is correct — you
    // cannot walk between floors through a shop.
    verticalMarkers.forEach((a) => {
        verticalMarkers.forEach((b) => {
            if (a.floor === b.floor) return;
            if (Math.abs(a.rank - b.rank) !== 1) return;
            if (Math.hypot(a.node.x - b.node.x, a.node.z - b.node.z) > CORE_LINK_METRES) return;
            graph.addEdge(a.node, b.node, 'vertical');
        });
    });

    if (!endpoints.length) return null;

    const landmarkAt = (pt, floorId) => {
        let best = null;
        let bestD = 30;
        landmarks.forEach((l) => {
            if (l.floor !== floorId) return;
            const d = Math.hypot(l.x - pt.x, l.z - pt.z);
            if (d < bestD) { bestD = d; best = l; }
        });
        return best ? best.label : null;
    };

    const router = createRouter({ graph, endpoints, floors, landmarkAt, simplifyTolerance: 2.2 });
    return {
        ...router,
        stats: {
            floors: walkableFloors,
            nodes: graph.nodes.size,
            endpoints: endpoints.length,
            verticalLinks: verticalMarkers.length,
        },
    };
}
